import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Pane — inline (non-modal) counterpart to ``Drawer*``.
 *
 * Shares the same visual chrome (header / body / footer + title /
 * description typography) as the Drawer family, but renders pure HTML —
 * no Radix Dialog context, no Portal, no overlay. Use this when a viewer
 * is mounted INLINE inside a workspace hub (``conversations.tsx`` /
 * ``calendar.tsx`` / ``documents.tsx``) rather than slid in from the
 * right via ``<Drawer>``.
 *
 * Why a sibling primitive instead of "just use ``<div>``":
 *   - DRY: hubs need the same header/body/footer rhythm, the same title
 *     font, the same description style as the drawer. Re-declaring those
 *     classes at every callsite drifts; a thin primitive keeps them
 *     in lockstep.
 *   - Correctness: ``DrawerTitle`` / ``DrawerDescription`` resolve to
 *     ``DialogPrimitive.Title`` / ``Description`` which REQUIRE a
 *     ``Dialog.Root`` ancestor — using them inline throws
 *     ``DialogTitle must be used within Dialog`` at runtime.
 *
 * If a viewer ever needs to flip between inline and drawer mode, the
 * caller wraps the same children with either ``<Drawer><DrawerContent>``
 * or nothing — both surfaces accept the same Pane* tags.
 */

// May 2026: switched to ``forwardRef`` so callers (e.g.
// ``InboxDetailPane``) can attach scroll-position listeners to the body
// for compact-on-scroll headers. The header itself also forwards the ref
// for future sticky animations that need a node reference.
const PaneHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col gap-1.5 border-hairline border-b px-5 pt-5 pb-3",
      className,
    )}
    {...props}
  />
));
PaneHeader.displayName = "PaneHeader";

const PaneBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 overflow-y-auto px-5 py-4", className)}
    {...props}
  />
));
PaneBody.displayName = "PaneBody";

const PaneFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col-reverse gap-2 border-hairline border-t px-5 py-4 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
));
PaneFooter.displayName = "PaneFooter";

const PaneTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      "font-display font-semibold text-base text-ink-1 leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
PaneTitle.displayName = "PaneTitle";

const PaneDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-ink-2 text-sm", className)} {...props} />
));
PaneDescription.displayName = "PaneDescription";

export { PaneHeader, PaneBody, PaneFooter, PaneTitle, PaneDescription };
